import { Injectable } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { DataObject } from '@shared/models/data-object.model';
import { Actions, ofAction, Store } from '@ngxs/store';
import { CurrentCatalogModel } from '@records/state/current-catalog/current-catalog.model';
import { CURRENT_DATA_STATE_TOKEN } from '@records/state/current-data/current-data-state.token';
import { combineLatest, defer, merge, Observable } from 'rxjs';
import { distinctUntilChanged, filter, map, startWith, switchMap, take } from 'rxjs/operators';
import { GeneralFormValidatorEntityService } from './general-form-validator-entity.service';
import { MainFormEvent, MainFormEventFactory } from './main-form.model';
import { FormMakerService } from '@controls/core/form-maker.service';
import { CatalogStateStreamsService } from '@records/state/current-catalog/catalog-state-streams.service';
import { CURRENT_RECORD_STATE_TOKEN } from '@records/state/current-record/current-record-state.token';
import { EntityType } from '@shared/models/entity-enum';
import { RecordType } from '@records/state/utils/current-record-type';
import { getFormConfigByType } from '@records/catalog/form/form-config';
import { FormControlOptions } from '@miq/catalog';
import { collapse } from '@shared/functions';
import { GetRecord } from '@records/state/current-data/get-record.action';
import { HasValidityCheck } from '@records/controls/record-details-tabs/save-validation.model';
import { CURRENT_CATALOG_STATE_TOKEN } from '@records/state/current-catalog/state-token';

/**
 * Service to build main form to minimize the workload of create/register new elements to the form at sub
 * and leaf levels.
 */
@Injectable({ providedIn: 'root' })
export class MainFormService implements HasValidityCheck {
  private _form$: Observable<MainFormEvent>;
  private _form: UntypedFormGroup = null;
  private readonly recordComparator = (a, b) => {
    if (a.record === b.record && a.recordLoaded === b.recordLoaded) return true;
    if (a.recordLoaded !== b.recordLoaded || !a.record || !b.record) return false;
    // miqProperties holds metadata only (clipboardId, favoriteId) — not form controls.
    // Skip it so clipboard/favourite actions don't trigger an unnecessary form rebuild,
    // which would cause summaryInfoStream$ to emit partial form data and overwrite
    // the server-preprocessed company_contact_role_addresses in the header display.
    const aRecord = a.record as Record<string, unknown>;
    const bRecord = b.record as Record<string, unknown>;
    const keys = new Set([...Object.keys(aRecord), ...Object.keys(bRecord)]);
    keys.delete('miqProperties');
    for (const key of keys) {
      if (aRecord[key] !== bRecord[key]) return false;
    }
    return true;
  };
  constructor(
    store: Store,
    actions: Actions,
    catalogStateStreams: CatalogStateStreamsService,
    private fb: UntypedFormBuilder,
    private formMakerService: FormMakerService,
    private generalFormValidatorService: GeneralFormValidatorEntityService
  ) {
    // get catalog loaded state.
    // Use defer() so the snapshot is taken at subscription time (not at service construction time).
    // This guarantees an immediate emission even when NGXS's internal shareReplay({ refCount: true })
    // buffer has been cleared (i.e. after all subscribers unsubscribed during component destroy /
    // re-navigation). When the catalog is already loaded at subscription time, the startWith seeds
    // combineLatest immediately; live state updates flow through loadedOrFetched$ afterwards.
    const catalog$ = defer(() => {
      const snapshot = store.selectSnapshot(CURRENT_CATALOG_STATE_TOKEN);
      return catalogStateStreams.loadedOrFetched$.pipe(
        startWith(snapshot.catalogLoaded ? snapshot : null),
        filter(Boolean),
        distinctUntilChanged()
      );
    });

    // get record loaded state — use select (not selectOnce) so recordChange$ always gets a live snapshot.
    // defer() + startWith(snapshot) for the same reason as catalog$: ensures immediate emission
    // on re-subscription when NGXS shareReplay buffer has been cleared.
    //
    // Compare only `record` and `recordLoaded` — NOT `associatedRecords`. Actions like
    // SetAssociatedPropertyAction patch `associatedRecords` (e.g. when the user picks a property
    // on a lease/availability form) without changing the underlying record. Without this comparator,
    // each such patch would cause _form$ to re-emit and rebuild the entire form from scratch,
    // wiping every value the user had already entered.
    const record$ = defer(() => {
      const snapshot = store.selectSnapshot(CURRENT_DATA_STATE_TOKEN);
      return store
        .select(CURRENT_DATA_STATE_TOKEN)
        .pipe(startWith(snapshot), distinctUntilChanged(this.recordComparator));
    });

    const view$ = defer(() => {
      const snapshot = store.selectSnapshot(CURRENT_RECORD_STATE_TOKEN);
      return store.select(CURRENT_RECORD_STATE_TOKEN).pipe(
        startWith(snapshot),
        map(curRecord => curRecord.view),
        distinctUntilChanged()
      );
    });

    const recordChange$ = actions.pipe(
      ofAction(GetRecord),
      switchMap(() => record$.pipe(take(1)))
    );
    // fork join and map to a form creation

    this._form$ = combineLatest([catalog$, merge(record$, recordChange$), view$]).pipe(
      /*
       * TODO: ELIAS to review auditTime usage.
       *  auditTime Commented as it is causing issues when navigating between entities (MIQ-25129).
       *  The issue: when navigating from the lease to the tenancy, the page and the components are
       *  getting created before the auditTime releases the last caught event. This last event contains the
       *  catalog of the lease and the tenancy record, which causes the logic in the map below to
       *  create a form using the lease catalog with a tenancy record.
       *  This causes the components from the tenancy page to fail because they are expecting a tenant record.
       *
       *  auditTime(100),
       */
      map(catalogRecord => {
        const [catalog, currentRecord, view] = catalogRecord;
        const entity = store.selectSnapshot(CURRENT_RECORD_STATE_TOKEN);
        if (!catalog?.catalogLoaded && currentRecord.recordLoaded) {
          // emit loading state ONLY
          return MainFormEventFactory.LoadingCatalogEvent();
        } else if (currentRecord.recordLoaded && view === 'details') {
          // create the form AND emit readiness
          this._form = this._makeForm(catalog, currentRecord.record, { entity: entity.type });

          this.generalFormValidatorService.connect({ control: this._form });

          return MainFormEventFactory.FormReadyEvent(this._form);
        }
        // ELSE: destroy the form (if any) AND emit loading record (catalog not loaded, record not loaded)
        if (this._form != null) {
          this._form = null;
        }
        return MainFormEventFactory.LoadingRecordEvent();
      })
    );
  }

  private _makeForm(catalog: CurrentCatalogModel, record: DataObject, options: { entity: RecordType }) {
    const formConfigs = getFormConfigByType(options.entity as EntityType);
    const virtualControls = this._getRootControlTypeFromFormControlOptions(record, formConfigs);
    const rootAttrGroup = this._makeRootAttrs(catalog, record);
    const mainForm = this.fb.group({ ...rootAttrGroup, ...virtualControls });
    this._makeNestedFormElements(catalog, record, mainForm);
    return mainForm;
  }

  /**
   * Returns a form group based on root level entries of type  'control' that are not present in
   * controlsConfig of sections.
   * @param record
   * @param formConfigs
   * @returns
   */
  private _getRootControlTypeFromFormControlOptions(
    record: DataObject,
    formConfigs: { [key: string]: FormControlOptions<any> }
  ) {
    const toFormGroup = formCtrlOpt => ({
      [formCtrlOpt.name]: [record[formCtrlOpt.name], formCtrlOpt.validatorFn ?? []],
    });
    const rootControlTypes = Object.values(formConfigs)
      .filter(formCtrlOptions => formCtrlOptions.type === 'control')
      .map(toFormGroup);
    return collapse(rootControlTypes);
  }

  /**
   * Creates formGroup entry object for root level attributes
   * @param catalog
   * @param record
   * @returns
   */
  private _makeRootAttrs(catalog: CurrentCatalogModel, record: DataObject): any {
    const rootAttrColumns = Object.values(catalog.columns);
    return this.formMakerService.makeColumnsFormGroup(rootAttrColumns, record);
  }

  /**
   * Utilizes default FormContainerControlBuilder service to create any sub-element
   * @param catalog
   * @param record
   * @param form
   */
  private _makeNestedFormElements(catalog: CurrentCatalogModel, record: DataObject, form: UntypedFormGroup): void {
    const allSections = Object.values(catalog.sections);
    this.formMakerService.createSectionsForm(allSections, form, record);
  }

  /**
   * Returns observable of form
   */
  get form$() {
    return this._form$;
  }

  /**
   * Access the created form, after form is resolved
   */
  get mainForm() {
    return this._form;
  }

  public markFormAsPristine() {
    this._form.markAsPristine();
    this._form.markAsUntouched();
    this._form.updateValueAndValidity();
  }

  /**
   * Method implementation from {@link HasValidityCheck}
   */
  validityCheck(): boolean {
    return this.mainForm && this.mainForm.valid;
  }
}
